import React from "react";
import { Link } from "react-router-dom";
import SEOHead from "./SEOHead";
import { medicalProceduresSchema, servicesFaqSchema } from "../data/seoSchemas";
import "./Services.css";

const Services = ({ showAll = false }) => {
  const coreServices = [
    {
      icon: "fa-solid fa-bone",
      title: "Knee Arthroscopy & ACL",
      description: "Minimally invasive keyhole procedures for ACL, PCL, and complex meniscus tears with accelerated rehabilitation.",
    },
    {
      icon: "fa-solid fa-robot",
      title: "Robotic Knee Replacement",
      description: "State-of-the-art robotic and computer-assisted total and partial knee replacement ensuring sub-millimeter precision.",
    },
    {
      icon: "fa-solid fa-person-running",
      title: "Shoulder Arthroscopy",
      description: "Advanced surgical stabilization for recurrent shoulder dislocations, Bankart tears, and rotator cuff tendon repair.",
    },
    {
      icon: "fa-solid fa-shield-virus",
      title: "Reverse Shoulder Replacement",
      description: "Anatomical and reverse total shoulder arthroplasty for severe cuff-tear arthropathy and complex proximal fractures.",
    },
    {
      icon: "fa-solid fa-syringe",
      title: "Joint Injections & Preservation",
      description: "Targeted hyaluronic acid and biological intra-articular therapies to relieve early osteoarthritis and restore cartilage health.",
    },
    {
      icon: "fa-solid fa-kit-medical",
      title: "Orthopedic Trauma Surgery",
      description: "Comprehensive emergency management of peri-articular fractures, multi-ligament injuries, and revision reconstructions.",
    },
  ];

  const fullCategories = [
    {
      category: "Knee Arthroscopic Surgery (Keyhole)",
      items: [
        { title: "ACL Reconstruction", desc: "Anatomic single and double-bundle anterior cruciate ligament repair using modern graft fixation." },
        { title: "Meniscal Repair & Preservation", desc: "All-inside and inside-out meniscus suturing to preserve shock-absorption and prevent early arthritis." },
        { title: "PCL Reconstruction", desc: "Posterior cruciate ligament reconstruction for severe posterior and rotatory knee instability." },
        { title: "Multi-Ligament Knee Reconstruction", desc: "Single-stage reconstruction of combined ACL, PCL, MCL, and posterolateral corner (PLC) disruptions." },
        { title: "Patella Dislocation Surgery (MPFL)", desc: "Medial patellofemoral ligament reconstruction to stabilize recurring patellar subluxation." },
      ],
    },
    {
      category: "Knee Surgery & Replacement (Arthroplasty)",
      items: [
        { title: "Robotic Knee Replacement", desc: "Robotic-assisted bone cuts and real-time gap balancing for faster walking and natural joint feel." },
        { title: "Computer Navigated Total Knee", desc: "Optical navigation alignment system ensuring ideal implant longevity and reduced wear." },
        { title: "Unicondylar (Partial) Knee Replacement", desc: "Minimally invasive replacement of isolated inner/outer compartments preserving healthy bone." },
        { title: "High Tibial Osteotomy (HTO)", desc: "Joint-preserving angular realignment surgery for young active patients with early medial arthritis." },
        { title: "Revision Knee Arthroplasty", desc: "Specialist revision surgery for failed, loose, or painful previous knee replacements." },
      ],
    },
    {
      category: "Shoulder Arthroscopy & Reconstruction",
      items: [
        { title: "Shoulder Dislocation & Bankart Repair", desc: "Arthroscopic labral repair and bone block transfer (Latarjet) for recurrent shoulder instability." },
        { title: "Rotator Cuff Tendon Repair", desc: "Double-row suture bridge repair of supraspinatus tears under high-definition arthroscopic visualization." },
        { title: "Biceps Tenodesis / Tenotomy", desc: "Treatment of long head of biceps pathology causing chronic anterior shoulder pain." },
        { title: "Frozen Shoulder (Capsular Release)", desc: "Arthroscopic release of tight adhesive capsulitis restoring painless overhead mobility." },
      ],
    },
    {
      category: "Shoulder Replacement & Trauma",
      items: [
        { title: "Reverse Shoulder Replacement", desc: "Engineered biomechanical solution for elderly patients with cuff tear arthropathy." },
        { title: "Total Shoulder Replacement", desc: "Anatomic humeral head and glenoid resurfacing for severe osteoarthritis with intact tendons." },
        { title: "Complex Shoulder Trauma Surgery", desc: "Reconstruction of proximal humerus fractures, clavicle injuries, and AC joint dislocations." },
      ],
    },
  ];

  return (
    <section className="services-section section-padding" id="services">
      {showAll && (
        <SEOHead
          title="Surgical Specialties & Procedures | Dr. Sunil Apsingi Hyderabad"
          description="Advanced orthopedic procedures by Dr. Sunil Apsingi: Robotic Knee Replacement, Knee Arthroscopy, ACL Reconstruction, Shoulder Bankart Repair & Rotator Cuff Surgery in Hyderabad."
          canonicalPath="/services"
          schemaJson={[medicalProceduresSchema, servicesFaqSchema]}
          breadcrumbs={[
            { name: "Home", path: "/" },
            { name: "Surgical Specialties", path: "/services" },
          ]}
        />
      )}
      <div className="container">
        <div className="text-center mb-5">
          <span className="section-header-pill">
            <i className="fa-solid fa-hospital-symbol me-1"></i> Surgical Specializations
          </span>
          <h2 className="section-title-large">
            {showAll ? "Comprehensive Surgical Treatments" : "Specialized Orthopedic Procedures"}
          </h2>
          <p className="section-subtitle-muted">
            State-of-the-art keyhole arthroscopy, joint reconstruction, and robotic procedures led by Dr. Sunil Apsingi.
          </p>
        </div>

        {/* Core Procedures Grid */}
        <div className="row g-4">
          {coreServices.map((service, index) => (
            <div key={index} className="col-lg-4 col-md-6">
              <div className="service-card-modern h-100 p-4">
                <div className="service-icon-bubble mb-3">
                  <i className={service.icon}></i>
                </div>
                <h4 className="service-card-title mb-2">{service.title}</h4>
                <p className="service-card-desc mb-4">{service.description}</p>
                <div className="mt-auto">
                  <Link to="/bookappointment" className="service-book-link">
                    Book Assessment <i className="fa-solid fa-arrow-right ms-1"></i>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Detailed Breakdown when showAll is true */}
        {showAll && (
          <div className="mt-5 pt-4">
            {fullCategories.map((cat, catIdx) => (
              <div key={catIdx} className="category-section-block mb-5">
                <h3 className="category-block-title mb-4">
                  <i className="fa-solid fa-circle-check text-teal me-2"></i>
                  {cat.category}
                </h3>
                <div className="row g-3">
                  {cat.items.map((item, itemIdx) => (
                    <div key={itemIdx} className="col-md-6">
                      <div className="sub-procedure-card p-3 rounded h-100">
                        <h5 className="procedure-title mb-1">{item.title}</h5>
                        <p className="procedure-desc small text-muted mb-0">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Patient FAQs Section (Matching Schema.org FAQPage) */}
            <div className="faq-section-block mt-5 pt-4">
              <div className="text-center mb-4">
                <span className="section-header-pill">
                  <i className="fa-regular fa-circle-question me-1"></i> Frequently Asked Questions
                </span>
                <h3 className="section-title-large">Common Clinical & Surgical Inquiries</h3>
              </div>

              <div className="row justify-content-center">
                <div className="col-lg-10">
                  <div className="accordion" id="servicesFaqAccordion">
                    {servicesFaqSchema.mainEntity.map((faq, idx) => (
                      <div className="accordion-item mb-3 border rounded-3 overflow-hidden shadow-sm" key={idx}>
                        <h2 className="accordion-header" id={`faqHeading${idx}`}>
                          <button
                            className={`accordion-button ${idx !== 0 ? "collapsed" : ""}`}
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#faqCollapse${idx}`}
                            aria-expanded={idx === 0 ? "true" : "false"}
                            aria-controls={`faqCollapse${idx}`}
                          >
                            <span className="fw-semibold text-navy">{faq.name}</span>
                          </button>
                        </h2>
                        <div
                          id={`faqCollapse${idx}`}
                          className={`accordion-collapse collapse ${idx === 0 ? "show" : ""}`}
                          aria-labelledby={`faqHeading${idx}`}
                          data-bs-parent="#servicesFaqAccordion"
                        >
                          <div className="accordion-body text-secondary lh-base">
                            {faq.acceptedAnswer.text}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {!showAll && (
          <div className="text-center mt-5">
            <Link to="/services" className="btn-medical-secondary">
              Explore All Surgical Services <i className="fa-solid fa-arrow-right ms-2"></i>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default Services;
